import { nowInSeconds } from '@repo/utils'
import { concatHex, type Hex, numberToHex, size } from 'viem'

/**
 * Append a recoverable 8-byte calldata attribution suffix to a call's `data`:
 * a 4-byte client unix-seconds timestamp (a submission-time debug aid, not block
 * time) + the 4-byte pre-validated `originTag`. Contracts that ABI-decode their
 * args ignore trailing bytes, so on suffix-safe targets the suffix is inert and
 * recoverable from raw tx data by indexers (see
 * ADR-2026-05-27-calldata-attribution-tagging).
 *
 * The `size(data) < 4` guard is a non-throwing backstop: pure value transfers
 * (`0x`) and shorter-than-selector calldata are returned unchanged so a suffix
 * can never route a `receive()` call to `fallback()`. Callers own the
 * suffix-safety decision and report malformed calldata themselves.
 *
 * @param data The call's calldata.
 * @param originTag A pre-validated 4-byte `Hex` origin marker.
 * @returns The suffixed calldata, or `data` unchanged when it has no complete
 *   4-byte selector.
 */
export function addCalldataAttribution(data: Hex, originTag: Hex): Hex {
  if (size(data) < 4) return data

  const timestamp = numberToHex(nowInSeconds(), { size: 4 })
  return concatHex([data, timestamp, originTag])
}
